/**
 * Linked zoom across a group of charts — pure logic, no Chart.js import.
 *
 * Split from chart-kit for the same reason monitored-chart-data.ts is split
 * from monitored-chart.ts: the Chart.js zoom plugin pulls in hammerjs, which
 * needs a DOM, so anything importing it can't be unit-tested in node.
 */

/* ---------------------------------------------------------------------------
 * Linked zoom across a group of charts
 *
 * A page shows one trajectory chart per goal. Zooming one and leaving the rest
 * at full span is actively misleading: come back after a few minutes and the
 * stacked charts read as though they share an x-axis when they don't. So a
 * zoom or pan on any chart is applied to every sibling in its group.
 *
 * Two things this has to get right:
 *
 *  - GROUPS, not a global broadcast. state.activeCharts holds BOTH families —
 *    9-month trajectories and 28-day monitored-event charts. They have
 *    different domains and different limits; linking across them would have
 *    each clamping against the other's range.
 *
 *  - RE-ENTRANCY. Applying a range to a sibling fires that sibling's onZoom,
 *    which would broadcast back. `applying` short-circuits the echo.
 *
 * Cost: the broadcast is coalesced through requestAnimationFrame, so a gesture
 * that fires onZoom on every wheel tick still only repaints each sibling once
 * per frame.
 * ------------------------------------------------------------------------- */

export type SyncGroup = "trajectory" | "monitored";

/**
 * The surface the sync layer needs. Deliberately structural rather than
 * `Chart`: importing chart.js here would drag chartjs-plugin-zoom (and
 * hammerjs, which touches `window`) into every test that exercises this
 * logic. Same split as monitored-chart-data.ts vs monitored-chart.ts.
 */
export interface SyncableChart {
  scales?: { x?: { min: number; max: number } };
  zoomScale(axis: string, range: { min: number; max: number }, mode?: "none"): void;
  resetZoom(mode?: "none"): void;
}

interface GroupState {
  charts: SyncableChart[];
  /**
   * The group's DEFAULT view — what the charts opened on and what resetZoom()
   * returns to. OMON-308 made this narrower than the reachable data for the
   * trajectory group (today ± a month, out of a 12-month plan), so it is no
   * longer interchangeable with the pan/zoom limits and is named accordingly.
   */
  defaultView: { min: number; max: number };
  listeners: Set<(min: number, max: number, zoomed: boolean) => void>;
}

// requestAnimationFrame is a browser API. Fall back to a timer so this module
// stays usable (and testable) outside a DOM.
const schedule: (cb: () => void) => number =
  typeof requestAnimationFrame === "function"
    ? (cb) => requestAnimationFrame(cb)
    : (cb) => setTimeout(cb, 16) as unknown as number;

const syncGroups = new Map<SyncGroup, GroupState>();
let applying = false;
let pendingFrame = 0;
let pendingRange: { group: SyncGroup; min: number; max: number } | null = null;

function groupState(group: SyncGroup): GroupState {
  let g = syncGroups.get(group);
  if (!g) {
    g = { charts: [], defaultView: { min: 0, max: 0 }, listeners: new Set() };
    syncGroups.set(group, g);
  }
  return g;
}

/** Drop every chart in a group. Call before rebuilding that family's canvases. */
export function clearSyncGroup(group: SyncGroup) {
  const g = groupState(group);
  g.charts = [];
}

/** Add a freshly-built chart to its group, recording the group's default view. */
export function registerForSync(group: SyncGroup, chart: SyncableChart, minMs: number, maxMs: number) {
  const g = groupState(group);
  g.charts.push(chart);
  g.defaultView = { min: minMs, max: maxMs };
}

/** Subscribe to range changes for a group. Returns an unsubscribe function. */
export function onRangeChange(
  group: SyncGroup,
  fn: (min: number, max: number, zoomed: boolean) => void,
): () => void {
  const g = groupState(group);
  g.listeners.add(fn);
  return () => g.listeners.delete(fn);
}

/**
 * True when the visible range has meaningfully moved off the group's default
 * view — narrower, wider, OR shifted.
 *
 * It used to test only "narrower than the full span", which was equivalent
 * while the default view WAS the full span. OMON-308 broke that equivalence:
 * the trajectory charts now open on today ± a month inside a 12-month
 * envelope, so zooming out to the whole plan makes the range wider, not
 * narrower — and the old test would have quietly hidden the badge and the
 * reset button exactly when the reader had lost the default and most needed
 * the way back to it.
 */
function isOffDefaultView(g: GroupState, min: number, max: number): boolean {
  if (!(g.defaultView.max - g.defaultView.min)) return false;
  // a whole day of slack, so float noise at rest doesn't read as moved
  const slack = 86_400_000;
  return Math.abs(min - g.defaultView.min) > slack || Math.abs(max - g.defaultView.max) > slack;
}

/* OMON-371: a reader's zoom survives a tab change.
 *
 * Leaving the Development Goals tab destroys its charts; coming back rebuilds
 * them on the default view, so a reader who had pinched out to see the whole
 * plan was put back on one month every time (Ed, 2026-10-09). The group now
 * remembers the last off-default range, keyed by whatever setSyncKey() was
 * given (the patient), and restoreRememberedView() puts it back after a
 * rebuild. Back on the default view (Reset, or zooming back) forgets it; a
 * different key forgets it. In memory only, nothing is persisted. */
const syncKeys = new Map<SyncGroup, string>();
const remembered = new Map<SyncGroup, { key: string; min: number; max: number }>();

/** Name what the group is showing (e.g. the patient). A new key drops the old memory. */
export function setSyncKey(group: SyncGroup, key: string) {
  syncKeys.set(group, key);
  const r = remembered.get(group);
  if (r && r.key !== key) remembered.delete(group);
}

/** Re-apply the remembered range (if any, for the current key) to every chart in the group. */
export function restoreRememberedView(group: SyncGroup) {
  const r = remembered.get(group);
  const g = groupState(group);
  if (!r || r.key !== (syncKeys.get(group) ?? "") || !g.charts.length) return;
  applying = true;
  try {
    for (const c of g.charts) c.zoomScale("x", { min: r.min, max: r.max }, "none");
  } finally {
    applying = false;
  }
  notify(group, r.min, r.max);
}

function notify(group: SyncGroup, min: number, max: number) {
  const g = groupState(group);
  const zoomed = isOffDefaultView(g, min, max);
  if (zoomed) remembered.set(group, { key: syncKeys.get(group) ?? "", min, max });
  else remembered.delete(group);
  for (const fn of g.listeners) fn(min, max, zoomed);
}

/** Broadcast `source`'s current x-range to its siblings, coalesced per frame. */
export function broadcastRange(group: SyncGroup, source: SyncableChart) {
  if (applying) return;
  const x = source.scales?.x;
  if (!x) return;
  pendingRange = { group, min: x.min, max: x.max };
  if (pendingFrame) return;
  pendingFrame = schedule(() => {
    pendingFrame = 0;
    const p = pendingRange;
    pendingRange = null;
    if (!p) return;
    const g = groupState(p.group);
    applying = true;
    try {
      for (const c of g.charts) {
        if (c === source) continue;
        const cx = c.scales?.x;
        if (!cx || (cx.min === p.min && cx.max === p.max)) continue;
        c.zoomScale("x", { min: p.min, max: p.max }, "none");
      }
    } finally {
      applying = false;
    }
    notify(p.group, p.min, p.max);
  });
}

/**
 * Reset every chart in a group back to its default view.
 *
 * Chart.js's resetZoom() restores the scale's configured min/max, which is the
 * default view by construction — so this stays correct without the sync layer
 * having to reapply a range itself.
 */
export function resetGroupZoom(group: SyncGroup) {
  const g = groupState(group);
  applying = true;
  try {
    for (const c of g.charts) c.resetZoom("none");
  } finally {
    applying = false;
  }
  notify(group, g.defaultView.min, g.defaultView.max);
}

/** Short label for a zoomed span: "12–19 Jul", or with the hour inside one day. */
export function formatRange(minMs: number, maxMs: number): string {
  const from = new Date(minMs);
  const to = new Date(maxMs);
  const day = (d: Date) => d.toLocaleDateString("en-GB", { day: "numeric", month: "short" });
  const time = (d: Date) => d.toLocaleTimeString("en-GB", { hour: "2-digit", minute: "2-digit" });
  if (maxMs - minMs <= 86_400_000 && from.toDateString() === to.toDateString()) {
    return `${day(from)}, ${time(from)}–${time(to)}`;
  }
  return `${day(from)} – ${day(to)}`;
}
